// Search Configuration - Controls for search parameters
// Allows users to configure scope, stereochemistry, and matcher options

import { Icon, Radio, Checkbox } from 'semantic-ui-react';
import '../../css/advanced-search.css';

export default function SearchConfiguration({ config, setConfig, pattern, setPattern }) {

  const handleScopeChange = (scope) => {
    const currentScope = config.carbuilder.searchScope;

    // Check if pattern needs to be cleared based on scope change
    let shouldClearMainChain = false;
    let shouldClearBranches = false;
    let warningMessage = '';

    if (currentScope === 'anywhere' && scope === 'mainChain') {
      // Switching to main chain only - need to clear branches
      if (pattern.branches && pattern.branches.length > 0) {
        shouldClearBranches = true;
        warningMessage = 'Switching to "Main Chain Only" will clear your branch patterns.';
      }
    } else if (currentScope === 'anywhere' && scope === 'branches') {
      // Switching to branches only - need to clear main chain
      if (pattern.mainChain && pattern.mainChain.length > 0) {
        shouldClearMainChain = true;
        warningMessage = 'Switching to "Branches Only" will clear your main chain pattern.';
      }
    } else if (currentScope === 'mainChain' && scope === 'branches') {
      // Switching from main chain to branches - clear main chain
      if (pattern.mainChain && pattern.mainChain.length > 0) {
        shouldClearMainChain = true;
        warningMessage = 'Switching to "Branches Only" will clear your main chain pattern.';
      }
    } else if (currentScope === 'branches' && scope === 'mainChain') {
      // Switching from branches to main chain - clear branches
      if (pattern.branches && pattern.branches.length > 0) {
        shouldClearBranches = true;
        warningMessage = 'Switching to "Main Chain Only" will clear your branch patterns.';
      }
    }

    // Show warning if needed
    if (warningMessage) {
      const confirmed = window.confirm(`${warningMessage}\n\nDo you want to continue?`);
      if (!confirmed) {
        return; // User cancelled
      }
    }

    // Update config
    setConfig(prev => ({
      ...prev,
      carbuilder: {
        ...prev.carbuilder,
        searchScope: scope
      }
    }));

    // Clear pattern if needed
    if (shouldClearMainChain || shouldClearBranches) {
      setPattern(prev => ({
        mainChain: shouldClearMainChain ? [] : prev.mainChain,
        branches: shouldClearBranches ? [] : prev.branches,
        bonds: shouldClearMainChain ? [] : prev.bonds
      }));
    }
  };

  const handleStereochemistryChange = (mode) => {
    setConfig(prev => ({
      ...prev,
      carbuilder: {
        ...prev.carbuilder,
        stereochemistryMode: mode
      }
    }));
  };

  const handleWildcardsToggle = () => {
    setConfig(prev => ({
      ...prev,
      carbuilder: {
        ...prev.carbuilder,
        enableWildcards: !prev.carbuilder.enableWildcards
      }
    }));
  };

  const handleBondStereochemistryChange = (mode) => {
    setConfig(prev => ({
      ...prev,
      carbuilder: {
        ...prev.carbuilder,
        bondStereochemistry: mode
      }
    }));
  };

  const resetToDefaults = () => {
    setConfig({
      carbuilder: {
        searchScope: 'anywhere',
        stereochemistryMode: 'agnostic',
        enableWildcards: true,
        bondStereochemistry: 'ignore',
        enabledMatchers: {
          mainChain: true,
          exactBranch: true,
          anywhere: true,
          wildcard: true
        }
      }
    });
  };

  return (
    <div className="search-configuration">
      <h3>
        <Icon name="settings" />
        Search Configuration
      </h3>

      {/* Search Scope */}
      <div className="config-section">
        <div className="config-header">
          <Icon name="target" />
          <strong>Search Scope</strong>
        </div>
        <div className="config-content">
          <div className="radio-option">
            <Radio
              label="Anywhere (Main Chain & Branches)"
              checked={config.carbuilder.searchScope === 'anywhere'}
              onChange={() => handleScopeChange('anywhere')}
            />
            <div className="option-description">
              Search across entire structure. Use for queries with branches like <code>Glc-Man[GlcA]</code> or simple patterns like <code>Glc-Man</code>
            </div>
          </div>

          <div className="radio-option">
            <Radio
              label="Main Chain Only"
              checked={config.carbuilder.searchScope === 'mainChain'}
              onChange={() => handleScopeChange('mainChain')}
            />
            <div className="option-description">
              Search only in the main carbohydrate chain. Use for main chain patterns like <code>Glc-Man-Gal</code> (no branches)
            </div>
          </div>

          <div className="radio-option">
            <Radio
              label="Branches Only"
              checked={config.carbuilder.searchScope === 'branches'}
              onChange={() => handleScopeChange('branches')}
            />
            <div className="option-description">
              Search only within branch structures. Use for branch patterns like <code>[GlcA]</code> or <code>[Glc-Man]</code>
            </div>
          </div>
        </div>
      </div>

      {/* Stereochemistry Mode */}
      <div className="config-section">
        <div className="config-header">
          <Icon name="dna" />
          <strong>Stereochemistry</strong>
        </div>
        <div className="config-content">
          <div className="radio-option">
            <Radio
              label="Agnostic (Any α/β, D/L)"
              checked={config.carbuilder.stereochemistryMode === 'agnostic'}
              onChange={() => handleStereochemistryChange('agnostic')}
            />
            <div className="option-description">
              Matches base sugar regardless of stereochemistry (default)
            </div>
          </div>

          <div className="radio-option">
            <Radio
              label="Strict (Exact Match)"
              checked={config.carbuilder.stereochemistryMode === 'strict'}
              onChange={() => handleStereochemistryChange('strict')}
            />
            <div className="option-description">
              Requires exact stereochemistry match (e.g., α-D-Glc)
            </div>
          </div>
        </div>
      </div>

      {/* Wildcard Support */}
      <div className="config-section">
        <div className="config-header">
          <Icon name="asterisk" />
          <strong>Wildcard Support</strong>
        </div>
        <div className="config-content">
          <Checkbox
            label="Enable wildcard (*) matching"
            checked={config.carbuilder.enableWildcards}
            onChange={handleWildcardsToggle}
            toggle
          />
          <div className="option-description">
            Wildcard (*) matches any single sugar in the pattern
          </div>
        </div>
      </div>

      {/* Bond Stereochemistry */}
      <div className="config-section">
        <div className="config-header">
          <Icon name="linkify" />
          <strong>Bond Stereochemistry</strong>
        </div>
        <div className="config-content">
          <div style={{
            padding: '10px',
            background: '#f8f9fa',
            borderRadius: '4px',
            marginBottom: '15px',
            fontSize: '0.9em',
            color: '#555'
          }}>
            <Icon name="info circle" />
            Bond matching is automatically enabled when your query includes bond notation (e.g., <code>Glc-b4-Man</code>)
          </div>

          <div className="radio-option">
            <Radio
              label="Ignore anomeric (any α/β)"
              checked={(config.carbuilder.bondStereochemistry || 'ignore') === 'ignore'}
              onChange={() => handleBondStereochemistryChange('ignore')}
            />
            <div className="option-description">
              Match linkage position only. Query <code>Glc-4-Man</code> matches both α and β at position 4
            </div>
          </div>

          <div className="radio-option">
            <Radio
              label="Strict anomeric (α/β must match)"
              checked={(config.carbuilder.bondStereochemistry || 'ignore') === 'strict'}
              onChange={() => handleBondStereochemistryChange('strict')}
            />
            <div className="option-description">
              Require exact anomeric configuration. Query <code>Glc-b4-Man</code> matches only β at position 4
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Summary */}
      <div className="config-summary">
        <Icon name="info circle" />
        <div className="summary-content">
          <strong>Current Settings:</strong>
          <ul>
            <li>Scope: <strong>{config.carbuilder.searchScope}</strong></li>
            <li>Stereochemistry: <strong>{config.carbuilder.stereochemistryMode}</strong></li>
            <li>Wildcards: <strong>{config.carbuilder.enableWildcards ? 'Enabled' : 'Disabled'}</strong></li>
          </ul>
        </div>
      </div>
    </div>
  );
}
